import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ArtifactAssemblyTypes, AssemblyArtifact } from '../models/artifact';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { DeveloperService } from '../services/developer.service';
import { GridService } from '../../shared/grid/grid.service';
import { TrackingUiAction } from '../models/tracking-ui-action';
import { DataFieldGroup } from '../../shared/datafields/models';
import { UntypedFormGroup } from '@angular/forms';
import { EnumFlagKeyValue, EnumFlagsService } from 'src/app/shared/enum-flags/enum-flags-service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule, DxTagBoxModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DeveloperArtifactAssemblyPickerComponent } from '../developer-artifacts/developer-artifact-assembly-picker.component';

import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { ColumnEditCellTemplateData, RowValidatingEvent } from 'devextreme/ui/data_grid';

@Component({
  selector: 'app-developer-tracking-ui-actions',
  templateUrl: './developer-tracking-ui-actions.component.html',
  styleUrls: ['./developer-tracking-ui-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, DeveloperArtifactAssemblyPickerComponent, DataFieldGroupContainerComponent, DxTagBoxModule]
})
export class DeveloperTrackingUiActionsComponent implements OnInit {
  allowAddingRow = false;
  allowUpdatingRow = false;
  allowDeletingRow = false;
  allowShowLog = false;

  artifacts: AssemblyArtifact[] = [];
  trackingUiActions: TrackingUiAction[] = [];
  trackingUiActionDisplayGroups: DataFieldGroup[] = [];
  trackingUiActionFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  buttonLocationFlags: EnumFlagsService;
  buttonLocationNames: string[];

  currentButtonLocations: string[] = [];

  constructor(
    private developerService: DeveloperService,
    private toastService: ToastService,
    private currentUserService: CurrentUserService,
    private loadingPanelService: LoadingPanelService,
    private developerArtifactService: DeveloperArtifactService,
    private gridService: GridService,
    public bizGridFiltervalues: BizGridFiltervalues) {

    this.buttonLocationFlags = new EnumFlagsService([
      new EnumFlagKeyValue('Document details page', 1),
      new EnumFlagKeyValue('Tracking result page', 2),
      new EnumFlagKeyValue('Tracking right-click menu', 4)]);
    this.buttonLocationNames = this.buttonLocationFlags.getAllNames();
  }

  ngOnInit(): void {
    this.allowAddingRow = this.currentUserService.hasPermission('Developer_Action_TrackingUiAction_Create');
    this.allowUpdatingRow = this.currentUserService.hasPermission('Developer_Action_TrackingUiAction_Edit');
    this.allowDeletingRow = this.currentUserService.hasPermission('Developer_Action_TrackingUiAction_Delete');
    this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');

    void this.refresh();
  }

  displayExprArtifact: (e: Parameters<NonNullable<DxDataGridTypes.Column['customizeText']>>[0]) => string =
    (e: Parameters<NonNullable<DxDataGridTypes.Column['customizeText']>>[0]) => {
      const artifactId = e.value === null || e.value === undefined ? '' : String(e.value);
      const artifact = this.artifacts?.find(a => a.ArtifactId === artifactId);
      return artifact ? `${artifact.Name} [${artifact.ClassName}] [${artifact.Filename}]` : 'Unknown...';
    };

  displayExprButtonLocations: (e: Parameters<NonNullable<DxDataGridTypes.Column['customizeText']>>[0]) => string =
    (e: Parameters<NonNullable<DxDataGridTypes.Column['customizeText']>>[0]) =>
      this.buttonLocationFlags.getNames(Number(e.value) || 0).join('; ');

  async refresh(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading tracking UI actions...');

      [
        this.artifacts,
        this.trackingUiActions,
      ] = await Promise.all([
        this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeTrackingAction]),
        this.developerService.getTrackingUiActions(),
      ]);
    } finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  onInitNewRow(e: DxDataGridTypes.InitNewRowEvent<TrackingUiAction>): void {
    this.trackingUiActionDisplayGroups = [];
    this.currentButtonLocations = [];
    e.data = { Id: null, Name: '', Description: '', ButtonLocations: 0 } as TrackingUiAction;
  }

  onRowInserting = (e: DxDataGridTypes.RowInsertingEvent<TrackingUiAction>): void => {
    e.cancel = this.gridService.handleDataGridRow(e.data, item => this.saveTrackingUiAction(item));
  };

  onRowUpdating = (e: DxDataGridTypes.RowUpdatingEvent<TrackingUiAction>): void => {
    e.cancel = this.gridService.handleDataGridRow({ ...e.oldData, ...e.newData }, item => this.saveTrackingUiAction(item), e.newData);
  };

  onRowRemoving = (e: DxDataGridTypes.RowRemovingEvent<TrackingUiAction>): void => {
    e.cancel = this.gridService.handleDataGridRow(e.data, item => this.removeTrackingUiAction(item));
  };

  onEditingStart = (e: DxDataGridTypes.EditingStartEvent<TrackingUiAction>): void => {
    void this.onEditingStartAsync(e);
  };

  onRowValidating(e: RowValidatingEvent<TrackingUiAction>): void {
    if (this.trackingUiActionFormGroup.invalid) {
      e.isValid = false;
      e.errorText = 'Please correct the errors in the form.';
    }
  }

  async onEditingStartAsync(e: DxDataGridTypes.EditingStartEvent<TrackingUiAction>): Promise<void> {
    const current: TrackingUiAction = e.data;
    this.currentButtonLocations = this.buttonLocationFlags.getNames(current.ButtonLocations);
    this.trackingUiActionDisplayGroups = [];
    this.trackingUiActionDisplayGroups = await this.developerService.getTrackingUiActionDataFieldGroups(current.ArtifactId, current.Id);
  }

  async saveTrackingUiAction(trackingUiAction: TrackingUiAction): Promise<TrackingUiAction> {
    const datafieldValues = DataFieldGroup.getDataFieldValues(this.trackingUiActionDisplayGroups, false);
    const newTrackingUiAction = await this.developerService.saveTrackingUiAction(trackingUiAction, datafieldValues);
    this.toastService.showToast('Tracking UI action saved...', ToastType.success);
    return newTrackingUiAction;
  }

  async removeTrackingUiAction(trackingUiAction: TrackingUiAction): Promise<TrackingUiAction> {
    await this.developerService.deleteTrackingUiAction(trackingUiAction.Id);
    this.toastService.showToast('Tracking UI action deleted...', ToastType.success);
    return trackingUiAction;
  }

  async setArtifactId(artifactId: string, cellInfo: ColumnEditCellTemplateData<TrackingUiAction, string>): Promise<void> {
    const setValue: (value: string) => void = cellInfo.setValue as (value: string) => void;
    setValue(artifactId || '');
    if (cellInfo.data.ArtifactId !== artifactId && artifactId) { // Only check artifact if it has changed!
      try {
        this.loadingPanelService.showLoadingPanelDirect('Verifying artifact...');
        this.trackingUiActionDisplayGroups = await this.developerService.getTrackingUiActionDataFieldGroups(artifactId);
      } finally {
        this.loadingPanelService.hideLoadingPanel();
      }
    }
  }

  setButtonLocations(cellInfo: ColumnEditCellTemplateData<TrackingUiAction, number>): void {
    const setValue: (value: number) => void = cellInfo.setValue as (value: number) => void;
    setValue(this.buttonLocationFlags.getValue(this.currentButtonLocations));
  }
}
